// Typed path-builders. Each returns a readonly tuple suitable for spreading into
// Firebase v9+ modular APIs: `doc(db, ...recipientDocPath(uid, id))`.
//
// Using `as const` tuples keeps the segment count and types known at the call site.

export const COLLECTION_THEA_WEB_USER = 'theaWebUser';
export const COLLECTION_RECIPIENT = 'recipient';
export const COLLECTION_RECOMMENDATION = 'recommendation';
export const COLLECTION_GIFT_ACTIVITY = 'giftActivity';
export const COLLECTION_THEA_WEB_MERGE_AUDIT = 'theaWebMergeAudit';
export const COLLECTION_MATERIALIZED_COMING_UP = 'materializedComingUp';
export const COLLECTION_MATERIALIZED_PEOPLE = 'materializedPeople';
export const COLLECTION_ALERT = 'alert';
// Top-level (per-product) precomputed "More like this" rail. One doc per
// product keyed by productId. Read-only for the FE; written by the BE
// materialization trigger + backfill.
export const COLLECTION_MATERIALIZED_SIMILAR_PRODUCTS = 'materializedSimilarProducts';
// Top-level (per-product) lean card-display projection of a `product` doc
// (THE-989): only the ~15 fields guide/hub card renderers read, NEVER
// `openai_embedding` (~30KB/doc, backend-KNN-only). Read-only for the FE,
// which reads this instead of `product` for every display-only lookup;
// written by the BE materialization trigger + backfill.
export const COLLECTION_PRODUCT_CARD = 'theaWebProductCard';
// The full catalog doc `COLLECTION_PRODUCT_CARD` is a lean projection of.
// `loadGuideItems` falls back to this, by id, ONLY for ids absent from the
// card projection (THE-989 review, 2026-10-16) — a brand-new product has no
// card until the next nightly `theaWebRefreshProductCards` sweep.
export const COLLECTION_PRODUCT = 'product';

// Singleton id under each materialized collection. The collection has
// one doc per user keyed by this constant so the FE can do a single-doc
// onSnapshot. Mirrors iOS-CRM materializedCalendar's pattern.
export const MATERIALIZED_COMING_UP_DOC_ID = 'current';
export const MATERIALIZED_PEOPLE_DOC_ID = 'current';

// User
export const theaWebUserDocPath = (uid: string) =>
  [COLLECTION_THEA_WEB_USER, uid] as const;

// Recipient
export const recipientCollectionPath = (uid: string) =>
  [COLLECTION_THEA_WEB_USER, uid, COLLECTION_RECIPIENT] as const;

export const recipientDocPath = (uid: string, recipientId: string) =>
  [COLLECTION_THEA_WEB_USER, uid, COLLECTION_RECIPIENT, recipientId] as const;

// Recommendation
export const recommendationCollectionPath = (uid: string, recipientId: string) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_RECIPIENT,
    recipientId,
    COLLECTION_RECOMMENDATION,
  ] as const;

export const recommendationDocPath = (
  uid: string,
  recipientId: string,
  recommendationId: string,
) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_RECIPIENT,
    recipientId,
    COLLECTION_RECOMMENDATION,
    recommendationId,
  ] as const;

// GiftActivity
export const giftActivityCollectionPath = (uid: string, recipientId: string) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_RECIPIENT,
    recipientId,
    COLLECTION_GIFT_ACTIVITY,
  ] as const;

export const giftActivityDocPath = (
  uid: string,
  recipientId: string,
  productId: string,
) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_RECIPIENT,
    recipientId,
    COLLECTION_GIFT_ACTIVITY,
    productId,
  ] as const;

// MergeAudit (top-level)
export const theaWebMergeAuditCollectionPath = () =>
  [COLLECTION_THEA_WEB_MERGE_AUDIT] as const;

export const theaWebMergeAuditDocPath = (auditId: string) =>
  [COLLECTION_THEA_WEB_MERGE_AUDIT, auditId] as const;

// MaterializedComingUp — one doc per user keyed by the
// MATERIALIZED_COMING_UP_DOC_ID singleton. Read-only for the FE; the
// BE trigger writes it on relevant recipient changes.
export const materializedComingUpDocPath = (uid: string) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_MATERIALIZED_COMING_UP,
    MATERIALIZED_COMING_UP_DOC_ID,
  ] as const;

// MaterializedPeople — one doc per user. Two BE triggers (recipient
// writes + giftActivity writes) keep it fresh; the FE just reads.
// Replaces per-tile giftActivity listeners on the /people page.
export const materializedPeopleDocPath = (uid: string) =>
  [
    COLLECTION_THEA_WEB_USER,
    uid,
    COLLECTION_MATERIALIZED_PEOPLE,
    MATERIALIZED_PEOPLE_DOC_ID,
  ] as const;

// Alerts — per-user subcollection. Written by the nightly
// theaWebRecomputeAlerts scheduled function + the immediate-fire branch
// of the recipient trigger. Dismissed via the theaWebUpdateAlert callable.
export const alertCollectionPath = (uid: string) =>
  [COLLECTION_THEA_WEB_USER, uid, COLLECTION_ALERT] as const;

export const alertDocPath = (uid: string, alertId: string) =>
  [COLLECTION_THEA_WEB_USER, uid, COLLECTION_ALERT, alertId] as const;

// MaterializedSimilarProducts — one top-level doc per product. The BE
// materialization trigger + backfill keep it fresh; the FE reads it directly
// (falling back to the `similarProducts` dispatcher op when the doc is absent).
export const materializedSimilarProductsDocPath = (productId: string) =>
  [COLLECTION_MATERIALIZED_SIMILAR_PRODUCTS, productId] as const;

// ProductCard (THE-989) — one top-level doc per product, keyed by productId.
// The lean card-display projection of `product`; read this instead of
// `product` for any display-only lookup (guides, hub rows, board hints).
export const productCardDocPath = (productId: string) =>
  [COLLECTION_PRODUCT_CARD, productId] as const;

export const productCardCollectionPath = () => [COLLECTION_PRODUCT_CARD] as const;
